import Image from 'next/image';
import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export function FelixVanillaCollateralTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const marketId = useMemo(() => {
    return trigger.context?.marketId || '';
  }, [trigger.context]);

  const assetAddress = useMemo(() => {
    return trigger.context?.asset || '';
  }, [trigger.context]);

  const chainId = useMemo(() => {
    return trigger.context?.chainId || 999;
  }, [trigger.context]);

  const networkDisplay = useMemo(() => {
    const chainIconUrl = getChainIconUrl(chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt="HyperEVM" width={20} height={20} />}
        <span>HyperEVM</span>
      </div>
    );
  }, [chainId, getChainIconUrl]);

  const marketDisplay = useMemo(() => {
    if (!marketId) return '-';

    return (
      <Text variant="body-sm" className="tw:font-mono">
        {marketId}
      </Text>
    );
  }, [marketId]);

  const assetDisplay = useMemo(() => {
    if (!assetAddress) return '-';

    return <AddressChip address={assetAddress} chainId={chainId} />;
  }, [assetAddress, chainId]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Market" value={marketDisplay} />
      <KeyValueRow label="Asset (Price Reference)" value={assetDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
